// A5 — labiq's WheelGeneController (the gene node editor), envelopes pinned against the REAL
// controller (feature-tested in WheelGeneEditorTest), key for key.
import { ENV } from "../../config/runtimeEnv";

import { WheelGeneRef } from "../../types/geneCodes/Panel";
import { http } from "../../lib/http/client";

// Already ends in `/api` — the GxEApi rule; a second /api 404s every call.
const API_BASE_URL = ENV.VITE_LABIQ_SERVER || "";

export interface WheelGenePatch {
	gene_symbol?: string;
	registry_ref?: string | null;
	sort_order?: number | null;
	is_hidden?: boolean;
}

export interface WheelGeneRecord {
	id: number;
	trait_id: number;
	gene_symbol: string;
	registry_ref: string | null;
	sort_order: number | null;
	// How many staging rows the rename cascade rewrote — 0 for a metadata-only patch.
	affected_snp_count: number;
}

export interface WheelGeneCreate {
	gene_symbol: string;
	registry_ref?: string | null;
	sort_order?: number | null;
}

/** POST /traits/{id}/wheel-genes — R6's SNP-less gene node. A symbol already on the trait returns THAT row with `created: false`. */
export const createTraitGene = async (traitId: number, body: WheelGeneCreate): Promise<{ gene: WheelGeneRef; created: boolean }> => {
	const response = await http.post(`${API_BASE_URL}/traits/${traitId}/wheel-genes`, body);
	return { gene: response.data.data, created: Boolean(response.data.created) };
};

/** PATCH /wheel-genes/{id} — a gene_symbol change is a RENAME CASCADE onto every SNP string in the trait; a collision is a 422, never a merge. */
export const updateWheelGene = async (id: number, patch: WheelGenePatch): Promise<WheelGeneRecord> => {
	const response = await http.patch(`${API_BASE_URL}/wheel-genes/${id}`, patch);
	return response.data.data;
};

/** DELETE /wheel-genes/{id} — detaches every SNP naming this gene under its trait; a SNP-less node deletes with 0. */
export const deleteWheelGene = async (id: number): Promise<{ deleted_snp_count: number }> => {
	const response = await http.delete(`${API_BASE_URL}/wheel-genes/${id}`);
	return response.data.data;
};

/** PATCH /traits/{id}/wheel-genes/order — ordered_ids become sort_order 0..n-1; returns the trait's ring exactly as A4's `genes` serves it. */
export const reorderTraitGenes = async (traitId: number, orderedIds: number[]): Promise<WheelGeneRef[]> => {
	const response = await http.patch(`${API_BASE_URL}/traits/${traitId}/wheel-genes/order`, { ordered_ids: orderedIds });
	return response.data.data;
};
